/* AutoCareX legal content — exact wording transcribed from the official PDFs.
 * Block types consumed by LegalPage renderer:
 *   "string"          -> paragraph
 *   { sub }           -> H3 subsection heading
 *   { label }         -> small bold caption
 *   { list: [] }      -> bullet list
 *   { note }          -> highlighted callout ("Important:" style)
 *   { strong }        -> emphasized standalone value line
 */

const COMPANY = {
  name: "JT Max India Enterprises",
  address: "440, F Block, Sector 63 Road, F Block, Sector 63, Noida, Chotpur, Uttar Pradesh - 201301, India",
  email: "help@autocarex.in",
  phone: "+91-87960 27362",
  website: "www.autocarex.in",
};

export const privacy = {
  slug: "privacy-policy",
  title: "Privacy Policy",
  metaTitle: "Privacy Policy",
  metaDescription:
    "AutoCareX Privacy Policy — how JT Max India Enterprises collects, uses, stores, discloses and safeguards your personal information.",
  updated: "June 2, 2026",
  intro: [
    'Welcome to AutoCareX ("Website", "Platform", "we", "our", or "us"), operated by JT Max India Enterprises, a partnership firm having its registered office at:',
    "440, F Block, Sector 63 Road, F Block, Sector 63, Noida, Chotpur, Uttar Pradesh - 201301, India",
    "At AutoCareX, we value your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, store, disclose, and safeguard your information when you visit or make purchases through www.autocarex.in.",
    "By accessing or using our Website, creating an account, placing an order, or otherwise interacting with our services, you consent to the practices described in this Privacy Policy.",
  ],
  sections: [
    { id: "information-we-collect", title: "1. Information We Collect", blocks: [
      "We may collect the following categories of information:",
      { sub: "A. Personal Information" },
      "When you register an account, place an order, contact customer support, or participate in promotional activities, we may collect:",
      { list: ["Full Name", "Email Address", "Mobile Number", "Billing Address", "Shipping Address", "Company Name (for B2B purchases)", "Optional vehicle details such as vehicle make, model, and year"] },
      { sub: "B. Transaction Information" },
      "When you purchase products through our Website, we may collect:",
      { list: ["Order details", "Purchase history", "Payment status", "Delivery information", "Refund and return information"] },
      { note: "Important: AutoCareX does not store credit card, debit card, UPI credentials, net banking credentials, or other sensitive payment information. All payments are processed securely through authorized third-party payment gateways." },
      { sub: "C. Automatically Collected Information" },
      "We may automatically collect:",
      { list: ["IP Address", "Browser type", "Device information", "Operating system", "Website usage data", "Referring URLs", "Session information", "Cookies and similar technologies"] },
      { sub: "D. User-Generated Content" },
      "We may collect information you voluntarily submit, including:",
      { list: ["Product reviews", "Ratings", "Testimonials", "Photographs uploaded with reviews", "Customer feedback"] },
      "Content submitted by users may become publicly visible on the Website.",
    ]},
    { id: "how-we-use-your-information", title: "2. How We Use Your Information", blocks: [
      "We use your information for legitimate business purposes including:",
      { list: ["Creating and managing user accounts", "Processing orders and payments", "Delivering purchased products", "Providing customer support", "Managing returns, refunds, and exchanges", "Improving our products and services", "Personalizing user experiences", "Sending order confirmations and updates", "Conducting analytics and performance measurement", "Preventing fraud and unauthorized activities", "Complying with legal obligations", "Sending promotional communications where permitted by law"] },
    ]},
    { id: "marketing-communications", title: "3. Marketing Communications", blocks: [
      "By creating an account or making a purchase, you may receive:",
      { list: ["Promotional emails", "Promotional SMS messages", "WhatsApp marketing communications", "Product recommendations", "Promotional offers and discounts", "Customer satisfaction surveys"] },
      "You may opt out of marketing communications at any time by:",
      { list: ["Clicking the unsubscribe link in emails", "Replying STOP where applicable", "Contacting our customer support team"] },
      "Transactional communications relating to orders, payments, deliveries, security, and account management may still be sent.",
    ]},
    { id: "cookies-and-tracking", title: "4. Cookies and Tracking Technologies", blocks: [
      "AutoCareX uses cookies and similar technologies to improve user experience and website performance.",
      "We may use:",
      { list: ["Google Analytics", "Meta Pixel", "Session Cookies", "Performance Cookies", "Advertising and Retargeting Technologies"] },
      "These technologies help us:",
      { list: ["Understand website traffic", "Improve website functionality", "Measure advertising effectiveness", "Deliver relevant advertisements"] },
      "Users may disable cookies through browser settings; however, certain website functionalities may become unavailable.",
    ]},
    { id: "third-party-payment-processors", title: "5. Third-Party Payment Processors", blocks: [
      "Payments on the Website are processed through authorized third-party payment service providers, including but not limited to:",
      { list: ["PhonePe Payment Gateway", "UPI Service Providers", "Card Processing Networks", "Banking Partners"] },
      "AutoCareX does not store complete payment card information.",
      "All payment processing is governed by the privacy policies and security practices of the respective payment providers.",
    ]},
    { id: "shipping-and-fulfillment-partners", title: "6. Shipping and Fulfillment Partners", blocks: [
      "To fulfill and deliver orders, we may share necessary customer information with logistics partners including:",
      { list: ["Delhivery", "Blue Dart"] },
      "Shared information may include:",
      { list: ["Name", "Phone Number", "Delivery Address", "Order Information"] },
      "Such information is shared solely for order fulfillment and delivery purposes.",
    ]},
    { id: "international-shipping", title: "7. International Shipping", blocks: [
      "While AutoCareX primarily serves customers within India, we may fulfill selected international orders. Where international shipping is available, customer information may be transferred to service providers located outside India solely for order processing and delivery purposes.",
    ]},
    { id: "data-sharing-and-disclosure", title: "8. Data Sharing and Disclosure", blocks: [
      "AutoCareX does not sell, rent, lease, or trade customer personal information to third parties.",
      "We may disclose information only under the following circumstances:",
      { sub: "A. Legal Requirements" },
      "When required by:",
      { list: ["Applicable laws", "Court orders", "Government authorities", "Regulatory agencies", "Law enforcement requests"] },
      { sub: "B. Business Protection" },
      "To:",
      { list: ["Prevent fraud", "Investigate security incidents", "Protect our legal rights", "Enforce our Terms and Conditions", "Protect customers and the public"] },
      { sub: "C. Business Restructuring" },
      "In the event of:",
      { list: ["Merger", "Acquisition", "Sale of assets", "Business transfer"] },
      "User information may form part of transferred business assets.",
    ]},
    { id: "user-reviews-and-public-content", title: "9. User Reviews and Public Content", blocks: [
      "Customers may post:",
      { list: ["Ratings", "Reviews", "Testimonials", "Images"] },
      "Any content voluntarily published may become publicly visible.",
      "Users are solely responsible for information they choose to disclose publicly.",
      "AutoCareX reserves the right to remove content that:",
      { list: ["Is unlawful", "Is misleading", "Is defamatory", "Violates intellectual property rights", "Violates community standards"] },
    ]},
    { id: "data-retention", title: "10. Data Retention", blocks: [
      "We retain personal information only for as long as reasonably necessary to:",
      { list: ["Fulfill orders", "Provide services", "Comply with legal obligations", "Resolve disputes", "Enforce agreements"] },
      "Certain records may be retained for longer periods where required under applicable tax, accounting, regulatory, or legal requirements.",
    ]},
    { id: "data-security", title: "11. Data Security", blocks: [
      "We implement commercially reasonable security measures to protect personal information against:",
      { list: ["Unauthorized access", "Misuse", "Disclosure", "Alteration", "Destruction"] },
      "However, no method of internet transmission or electronic storage is completely secure. Accordingly, AutoCareX cannot guarantee absolute security of information transmitted through the Website.",
    ]},
    { id: "your-rights", title: "12. Your Rights", blocks: [
      "Subject to applicable laws, users may:",
      { list: ["Access their personal information", "Request correction of inaccurate information", "Request deletion of personal information", "Withdraw marketing consent", "Request account closure"] },
      "Requests may be submitted to:",
      "Email: help@autocarex.in",
      "Deletion requests submitted following account closure will generally be processed within 30 days, subject to legal retention obligations.",
    ]},
    { id: "childrens-privacy", title: "13. Children's Privacy", blocks: [
      "AutoCareX products and services are intended for individuals who are at least 18 years of age.",
      "We do not knowingly collect personal information from children under 18 years of age.",
      "If we become aware that information from a minor has been collected, we may delete such information without notice.",
    ]},
    { id: "future-services", title: "14. Future Services", blocks: [
      "As our business evolves, AutoCareX may introduce:",
      { list: ["Loyalty Programs", "Affiliate Programs", "Subscription Services", "Wholesale Accounts", "Dealer Programs", "Franchise Opportunities", "Mobile Applications"] },
      "Additional privacy disclosures may be provided as required for such services.",
    ]},
    { id: "third-party-links", title: "15. Third-Party Links", blocks: [
      "The Website may contain links to third-party websites or services.",
      "AutoCareX is not responsible for:",
      { list: ["Privacy practices", "Security measures", "Content", "Policies"] },
      "of third-party websites.",
      "Users access third-party websites at their own risk.",
    ]},
    { id: "limitation-of-liability", title: "16. Limitation of Liability", blocks: [
      "To the maximum extent permitted by law, AutoCareX shall not be liable for:",
      { list: ["Unauthorized access to personal information", "Data breaches caused by third parties", "Internet failures", "Service interruptions", "Cyberattacks beyond reasonable control", "User negligence in protecting account credentials"] },
      "Users are responsible for maintaining the confidentiality of their login credentials.",
    ]},
    { id: "changes-to-this-privacy-policy", title: "17. Changes to This Privacy Policy", blocks: [
      "AutoCareX reserves the right to update, modify, or replace this Privacy Policy at any time.",
      'Updated versions will be posted on the Website with a revised "Last Updated" date.',
      "Continued use of the Website following publication of changes constitutes acceptance of the revised Privacy Policy.",
    ]},
    { id: "governing-law-and-jurisdiction", title: "18. Governing Law and Jurisdiction", blocks: [
      "This Privacy Policy shall be governed by and interpreted in accordance with the laws of India.",
      "Any disputes arising out of or relating to this Privacy Policy shall be subject to the exclusive jurisdiction of the competent courts located in Ghaziabad, Uttar Pradesh, India.",
    ]},
    { id: "contact-information", title: "19. Contact Information", blocks: [
      "For questions, concerns, complaints, or privacy-related requests, please contact:",
      { contact: true },
      "By using AutoCareX, you acknowledge that you have read, understood, and agreed to this Privacy Policy.",
    ]},
  ],
};

export const cookie = {
  slug: "cookie-policy",
  title: "Cookie Policy",
  metaTitle: "Cookie Policy",
  metaDescription:
    "AutoCareX Cookie Policy — how we use cookies and similar tracking technologies on www.autocarex.in.",
  updated: "June 2, 2026",
  intro: [
    'This Cookie Policy explains how AutoCareX ("Company", "we", "our", or "us"), operated by JT Max India Enterprises, uses cookies and similar tracking technologies when you visit or use www.autocarex.in ("Website").',
    "By continuing to use our Website, you accept essential cookies that are necessary for the site to function. Non-essential cookies (such as analytics and marketing) are used only after you provide explicit consent through our cookie banner, and you may change or withdraw your consent at any time using the \u201cCookie settings\u201d link in the footer.",
  ],
  sections: [
    { id: "who-we-are", title: "1. Who We Are", blocks: [
      "AutoCareX is an e-commerce platform specializing in automotive care and after-sales products for vehicles.",
      "Business Name: JT Max India Enterprises",
      "Registered Address:",
      "440, F Block, Sector 63 Road, F Block, Sector 63, Noida, Chotpur, Uttar Pradesh - 201301, India",
      "Email: help@autocarex.in",
      "Phone: +91-8796027362",
      "Website: www.autocarex.in",
    ]},
    { id: "what-are-cookies", title: "2. What Are Cookies?", blocks: [
      "Cookies are small text files stored on your computer, smartphone, tablet, or other device when you visit a website.",
      "Cookies help websites:",
      { list: ["Recognize users and devices", "Remember user preferences", "Improve website performance", "Analyze visitor behavior", "Provide personalized experiences", "Deliver relevant advertisements"] },
      "Cookies do not generally provide direct access to information stored on your device.",
    ]},
    { id: "types-of-cookies", title: "3. Types of Cookies We Use", blocks: [
      { sub: "A. Essential Cookies" },
      "These cookies are necessary for the Website to function properly.",
      "They enable:",
      { list: ["User authentication", "Secure account login", "Shopping cart functionality", "Checkout processes", "Order placement", "Security features", "Fraud prevention measures"] },
      "Without these cookies, certain Website functions may not operate correctly.",
      { label: "Examples" },
      { list: ["Login session cookies", "Cart cookies", "Security authentication cookies"] },
      { sub: "B. Performance and Analytics Cookies" },
      "These cookies help us understand how visitors interact with our Website.",
      "Information collected may include:",
      { list: ["Number of visitors", "Pages visited", "Traffic sources", "Time spent on pages", "User navigation behavior", "Website performance metrics"] },
      "This information is aggregated and used solely to improve our Website and customer experience.",
      { label: "Analytics Tools We Use" },
      { list: ["Google Analytics"] },
      "Google Analytics may collect:",
      { list: ["Device information", "Browser information", "IP address", "Usage statistics", "Geographic region information"] },
      { sub: "C. Functional Cookies" },
      "Functional cookies help remember user preferences and settings.",
      "These may include:",
      { list: ["Language preferences", "Location settings", "Account preferences", "Saved shopping cart information", "Previously viewed products"] },
      "These cookies improve convenience and user experience.",
      { sub: "D. Advertising and Marketing Cookies" },
      "Advertising cookies help us provide relevant advertisements and measure marketing effectiveness.",
      "These cookies may:",
      { list: ["Track visits across websites", "Measure advertising campaign performance", "Enable personalized advertising", "Limit repetitive advertisements", "Build audience segments"] },
      { label: "Marketing Technologies We Use" },
      { list: ["Meta Pixel (Facebook Pixel)", "Retargeting and Remarketing Technologies"] },
      "These technologies help us understand interactions with our advertisements and improve marketing campaigns.",
      { sub: "E. Third-Party Cookies" },
      "Certain third-party services integrated into our Website may place cookies on your device.",
      "Examples include:",
      { list: ["Payment providers", "Analytics providers", "Advertising platforms", "Social media integrations"] },
      "These third parties may collect information according to their own privacy policies.",
      "AutoCareX does not control the cookies placed by third-party services.",
    ]},
    { id: "information-collected", title: "4. Information Collected Through Cookies", blocks: [
      "Cookies may collect information such as:",
      { list: ["IP address", "Browser type", "Device type", "Operating system", "Session duration", "Website usage behavior", "Referral sources", "Advertising interactions", "Geographic location (approximate)", "Product browsing activity"] },
      "Cookies generally do not collect sensitive financial information such as:",
      { list: ["Credit card numbers", "Debit card numbers", "Banking credentials", "UPI PINS", "Payment passwords"] },
    ]},
    { id: "how-we-use-cookie-data", title: "5. How We Use Cookie Data", blocks: [
      "Information collected through cookies may be used to:",
      { list: ["Operate and secure the Website", "Process transactions", "Improve Website functionality", "Analyze user behavior", "Optimize product offerings", "Personalize user experiences", "Measure advertising effectiveness", "Detect suspicious or fraudulent activities", "Improve customer support services"] },
    ]},
    { id: "meta-pixel-and-remarketing", title: "6. Meta Pixel and Remarketing", blocks: [
      "AutoCareX uses Meta Pixel and other remarketing technologies.",
      "These technologies allow us to:",
      { list: ["Measure advertising performance", "Understand user interactions", "Build advertising audiences", "Deliver relevant advertisements on third-party platforms"] },
      "You may continue to see AutoCareX advertisements on websites, applications, and social media platforms after visiting our Website.",
      "This process does not provide AutoCareX with access to your private social media accounts or passwords.",
    ]},
    { id: "google-analytics", title: "7. Google Analytics", blocks: [
      "We use Google Analytics to better understand Website performance and visitor behavior.",
      "Google Analytics may collect information including:",
      { list: ["Pages viewed", "Time spent on pages", "User interactions", "Device and browser information", "Traffic source information"] },
      "This information helps us improve the Website, product offerings, and customer experience.",
    ]},
    { id: "cookie-retention-periods", title: "8. Cookie Retention Periods", blocks: [
      "Different cookies remain active for different periods.",
      { label: "Session Cookies" },
      "Session cookies expire automatically when you close your browser.",
      { label: "Persistent Cookies" },
      "Persistent cookies remain stored on your device until:",
      { list: ["They expire automatically;", "You delete them manually; or", "You clear browser data."] },
      "Retention periods vary depending on the purpose of the cookie.",
    ]},
    { id: "managing-cookies", title: "9. Managing Cookies", blocks: [
      "Most web browsers allow users to:",
      { list: ["View cookies", "Delete cookies", "Block cookies", "Restrict third-party cookies", "Receive notifications before cookies are stored"] },
      "You can manage cookie settings through your browser preferences.",
      "Please note that disabling cookies may affect:",
      { list: ["Login functionality", "Shopping cart functionality", "Checkout processes", "Website performance", "Personalized experiences"] },
      "Certain Website features may become unavailable if cookies are disabled.",
    ]},
    { id: "do-not-track-signals", title: "10. Do Not Track Signals", blocks: [
      'Some browsers provide a "Do Not Track" (DNT) feature.',
      "Because there is currently no universally accepted standard for responding to DNT signals, AutoCareX may not respond to such requests consistently across all systems and services.",
    ]},
    { id: "international-data-processing", title: "11. International Data Processing", blocks: [
      "Where applicable, information collected through cookies may be processed by service providers located outside India.",
      "By using our Website, you acknowledge that such processing may occur where necessary for analytics, advertising, security, or operational purposes.",
      "Reasonable safeguards are implemented to protect such information.",
    ]},
    { id: "data-security", title: "12. Data Security", blocks: [
      "AutoCareX takes commercially reasonable measures to protect information collected through cookies and related technologies.",
      "However, no internet-based technology can guarantee absolute security.",
      "Users acknowledge that transmission of information over the internet is undertaken at their own risk.",
    ]},
    { id: "changes-to-this-cookie-policy", title: "13. Changes to This Cookie Policy", blocks: [
      "We reserve the right to modify or update this Cookie Policy at any time.",
      "Changes will become effective immediately upon publication on the Website unless otherwise stated.",
      "Users are encouraged to review this Cookie Policy periodically.",
      "Continued use of the Website after updates constitutes acceptance of the revised Cookie Policy.",
    ]},
    { id: "contact-us", title: "14. Contact Us", blocks: [
      "If you have questions regarding this Cookie Policy or our use of cookies and tracking technologies, please contact:",
      { contact: true },
      "By using AutoCareX, you acknowledge that you have read, understood, and agreed to this Cookie Policy.",
    ]},
  ],
};

export const terms = {
  slug: "terms-and-conditions",
  title: "Terms & Conditions",
  metaTitle: "Terms & Conditions",
  metaDescription:
    "AutoCareX Terms & Conditions governing your use of www.autocarex.in, including purchases, products, services and promotions.",
  updated: "June 2, 2026",
  intro: [
    'Welcome to AutoCareX ("Website", "Platform", "Company", "we", "our", or "us"), operated by JT Max India Enterprises, having its registered office at:',
    "440, F Block, Sector 63 Road, F Block, Sector 63, Noida, Chotpur, Uttar Pradesh - 201301, India",
    'These Terms and Conditions ("Terms") govern your access to and use of www.autocarex.in, including all purchases, services, content, products, promotions, and interactions made through the Website.',
    "By accessing, browsing, registering an account, or placing an order on the Website, you acknowledge that you have read, understood, and agreed to be legally bound by these Terms.",
    "If you do not agree to these Terms, you must discontinue use of the Website immediately.",
  ],
  sections: [
    { id: "eligibility", title: "1. Eligibility", blocks: [
      "By using this Website, you represent and warrant that:",
      { list: ["You are at least 18 years of age.", "You possess legal capacity to enter into binding agreements.", "Information provided by you is accurate and complete.", "Your use of the Website complies with applicable laws and regulations."] },
      "AutoCareX reserves the right to refuse service, suspend access, or terminate accounts where eligibility requirements are not met.",
    ]},
    { id: "account-registration", title: "2. Account Registration", blocks: [
      "To purchase products through the Website, users must create an account.",
      "Users agree to:",
      { list: ["Provide accurate information.", "Maintain updated account details.", "Protect login credentials.", "Restrict unauthorized access to their account."] },
      "Users are solely responsible for all activities conducted through their account.",
      "AutoCareX shall not be liable for losses arising from unauthorized account access resulting from user negligence.",
    ]},
    { id: "products-and-services", title: "3. Products and Services", blocks: [
      "AutoCareX offers automotive care, detailing, maintenance, and after-sales products for vehicles.",
      "While we strive to ensure accuracy, we do not warrant that:",
      { list: ["Product descriptions are error-free.", "Product images perfectly match actual products.", "Product availability will remain uninterrupted."] },
      "Minor variations in packaging, labeling, appearance, and color may occur due to manufacturing updates and display settings.",
    ]},
    { id: "product-usage-disclaimer", title: "4. Product Usage Disclaimer", blocks: [
      "Customers are responsible for reading and following all instructions, warnings, and recommendations provided with each product.",
      "Improper use of automotive care products may result in:",
      { list: ["Surface damage", "Paint damage", "Finish deterioration", "Vehicle component damage"] },
      "AutoCareX shall not be liable for losses arising from:",
      { list: ["Improper product application", "Failure to follow instructions", "Use on unsuitable surfaces", "Incorrect product selection", "Third-party application or installation", "Negligent handling of products"] },
      "Users are encouraged to perform patch testing where applicable before full application.",
    ]},
    { id: "product-performance-disclaimer", title: "5. Product Performance Disclaimer", blocks: [
      "Certain products may advertise benefits such as:",
      { list: ["Scratch reduction", "Shine enhancement", "Surface protection", "Water repellency", "Restoration effects"] },
      "Actual results may vary based on:",
      { list: ["Vehicle condition", "Paint quality", "Surface preparation", "Application technique", "Environmental conditions", "Product maintenance practices"] },
      "Product descriptions are provided for informational purposes and do not constitute guarantees of specific outcomes.",
    ]},
    { id: "shelf-life-information", title: "6. Shelf Life Information", blocks: [
      "Certain AutoCareX products may carry an estimated shelf life of up to two (2) years when stored according to manufacturer recommendations.",
      "Shelf life does not constitute a product warranty, guarantee, or assurance of performance under all storage conditions.",
    ]},
    { id: "pricing", title: "7. Pricing", blocks: [
      "All prices displayed on the Website are subject to change without prior notice.",
      "AutoCareX reserves the right to:",
      { list: ["Modify pricing", "Correct pricing errors", "Withdraw offers", "Change discounts", "Amend promotional campaigns"] },
      "Errors in pricing, descriptions, or availability may be corrected at any time, including after an order has been placed.",
    ]},
    { id: "orders-and-acceptance", title: "8. Orders and Acceptance", blocks: [
      "Placing an order does not guarantee acceptance.",
      "AutoCareX reserves the right to refuse, limit, or cancel orders for reasons including:",
      { list: ["Product unavailability", "Pricing errors", "Fraud prevention", "Payment verification issues", "Shipping restrictions", "Regulatory concerns", "Suspected misuse of promotions"] },
      "If an order is cancelled after payment has been received, the applicable refund will be processed according to our Refund Policy.",
    ]},
    { id: "order-cancellations", title: "9. Order Cancellations", blocks: [
      "Customers may request cancellation of an order by contacting:",
      "Email: help@autocarex.in",
      "Cancellation requests will only be considered before the order has been dispatched.",
      "Once an order has been shipped, cancellation may not be possible and return procedures may apply instead.",
    ]},
    { id: "shipping-and-delivery", title: "10. Shipping and Delivery", blocks: [
      "Orders are generally processed within:",
      { strong: "24-48 business hours" },
      "Estimated delivery timelines within India are:",
      { strong: "3-5 business days" },
      "Delivery timelines are estimates and are not guaranteed.",
      "AutoCareX shall not be liable for delays caused by:",
      { list: ["Courier partners", "Weather conditions", "Public holidays", "Government restrictions", "Transportation disruptions", "Force majeure events"] },
      "Shipping services may be provided through logistics partners including:",
      { list: ["Delhivery", "Blue Dart"] },
    ]},
    { id: "returns-and-refunds", title: "11. Returns and Refunds", blocks: [
      "Returns and refunds are governed by the AutoCareX Delivery, Return and Refund Policy available on the Website.",
      "Return requests must comply with the conditions specified therein.",
      "Products that are opened, used, tampered with, damaged through misuse, or classified as non-returnable may not qualify for return or refund.",
    ]},
    { id: "payment-terms", title: "12. Payment Terms", blocks: [
      "AutoCareX accepts payment methods including:",
      { list: ["UPI", "Debit Cards", "Credit Cards", "Net Banking", "Digital Wallets", "Cash on Delivery (where available)"] },
      "Payments are processed through authorized third-party payment gateways.",
      "AutoCareX does not store complete payment card information or banking credentials.",
    ]},
    { id: "promotional-offers", title: "13. Promotional Offers", blocks: [
      "Promotional campaigns, coupons, discounts, rewards, and special pricing are subject to availability and may be:",
      { list: ["Modified", "Suspended", "Limited", "Withdrawn", "Cancelled"] },
      "at any time without prior notice.",
      "AutoCareX reserves sole discretion regarding interpretation and administration of promotional offers.",
    ]},
    { id: "user-reviews-and-content", title: "14. User Reviews and Content", blocks: [
      "Users may submit:",
      { list: ["Reviews", "Ratings", "Testimonials", "Photographs", "Feedback"] },
      "By submitting content, users grant AutoCareX a perpetual, worldwide, royalty-free, irrevocable license to:",
      { list: ["Use", "Reproduce", "Publish", "Modify", "Display", "Distribute"] },
      "such content for marketing, advertising, promotional, commercial, and business purposes.",
      "Users represent that submitted content:",
      { list: ["Is accurate", "Does not infringe third-party rights", "Does not violate applicable laws"] },
      "AutoCareX reserves the right to remove content at its discretion.",
    ]},
    { id: "intellectual-property-rights", title: "15. Intellectual Property Rights", blocks: [
      "All intellectual property associated with the Website remains the exclusive property of AutoCareX and/or its licensors.",
      "Protected content includes:",
      { list: ["Logos", "Trademarks", "Product names", "Product images", "Product descriptions", "Videos", "Graphics", "Website design", "Text content", "Software components"] },
      "No content may be copied, reproduced, distributed, modified, or exploited without prior written permission.",
    ]},
    { id: "prohibited-activities", title: "16. Prohibited Activities", blocks: [
      "Users shall not:",
      { list: ["Violate laws or regulations", "Commit fraud", "Submit false information", "Circumvent Website security", "Scrape Website content", "Upload malicious software", "Interfere with Website functionality", "Manipulate reviews or ratings", "Impersonate others"] },
      "Violations may result in account suspension, termination, legal action, or reporting to authorities.",
    ]},
    { id: "account-suspension-and-termination", title: "17. Account Suspension and Termination", blocks: [
      "AutoCareX reserves the right to suspend, restrict, or terminate accounts without prior notice where users engage in:",
      { list: ["Fraudulent activities", "Abuse of services", "Fake reviews", "Chargeback abuse", "Policy violations", "Harmful conduct"] },
      "Termination does not affect rights or obligations accrued prior to termination.",
    ]},
    { id: "limitation-of-liability", title: "18. Limitation of Liability", blocks: [
      "To the fullest extent permitted by applicable law, AutoCareX shall not be liable for:",
      { list: ["Indirect damages", "Consequential damages", "Special damages", "Incidental damages", "Loss of profits", "Loss of business opportunities", "Data loss", "Vehicle damage caused by misuse of products"] },
      "In all circumstances, AutoCareX's total liability arising from any claim shall not exceed the amount paid by the customer for the specific order giving rise to the claim.",
    ]},
    { id: "indemnification", title: "19. Indemnification", blocks: [
      "Users agree to indemnify and hold harmless AutoCareX, JT Max India Enterprises, its owners, employees, affiliates, agents, and service providers from claims, liabilities, damages, losses, costs, and expenses arising from:",
      { list: ["Violation of these Terms", "Misuse of products", "Violation of laws", "Infringement of third-party rights"] },
    ]},
    { id: "force-majeure", title: "20. Force Majeure", blocks: [
      "AutoCareX shall not be liable for delays, interruptions, or failures resulting from events beyond reasonable control, including:",
      { list: ["Natural disasters", "Floods", "Earthquakes", "Fires", "Strikes", "Lockouts", "Government actions", "Transportation disruptions", "Internet outages", "Cybersecurity incidents", "Utility failures", "Pandemic-related restrictions"] },
    ]},
    { id: "future-business-programs", title: "21. Future Business Programs", blocks: [
      "AutoCareX may introduce:",
      { list: ["Dealer Programs", "Distributor Accounts", "Wholesale Accounts", "Franchise Opportunities", "Subscription Services", "Affiliate Programs", "Loyalty Programs"] },
      "Additional terms may apply to such programs.",
    ]},
    { id: "privacy", title: "22. Privacy", blocks: [
      "Use of the Website is also governed by our:",
      { list: ["Privacy Policy", "Cookie Policy"] },
      "Users are encouraged to review these documents before using the Website.",
    ]},
    { id: "governing-law-and-jurisdiction", title: "23. Governing Law and Jurisdiction", blocks: [
      "These Terms shall be governed by and interpreted in accordance with the laws of India.",
      "Any dispute arising out of or relating to these Terms shall be subject to the exclusive jurisdiction of the competent courts located in Ghaziabad, Uttar Pradesh, India.",
    ]},
    { id: "modifications-to-terms", title: "24. Modifications to Terms", blocks: [
      "AutoCareX reserves the right to modify, amend, or replace these Terms at any time.",
      "Updated Terms shall become effective immediately upon publication on the Website unless otherwise stated.",
      "Continued use of the Website constitutes acceptance of the revised Terms.",
    ]},
    { id: "contact-information", title: "25. Contact Information", blocks: [
      "For questions regarding these Terms and Conditions, please contact:",
      { contact: true },
      "By accessing or using AutoCareX, you acknowledge that you have read, understood, and agreed to these Terms and Conditions.",
    ]},
  ],
};

export const delivery = {
  slug: "delivery-return-policy",
  title: "Delivery & Return Policy",
  metaTitle: "Delivery, Shipping, Return & Refund Policy",
  metaDescription:
    "AutoCareX Delivery, Shipping, Return & Refund Policy — order processing, shipping charges, returns, refunds and cancellations.",
  updated: "June 2, 2026",
  intro: [
    'This Delivery, Shipping, Return & Refund Policy ("Policy") governs the shipment, delivery, cancellation, return, replacement, and refund of products purchased through www.autocarex.in ("Website"), operated by JT Max India Enterprises ("AutoCareX", "Company", "we", "our", or "us").',
    "By placing an order on the Website, you acknowledge that you have read, understood, and agreed to this Policy.",
  ],
  sections: [
    { id: "company-information", title: "1. Company Information", blocks: [
      { contact: true },
    ]},
    { id: "order-processing", title: "2. Order Processing", blocks: [
      "All orders are subject to verification, acceptance, stock availability, and successful payment authorization.",
      "Orders are generally processed and dispatched within:",
      { strong: "24 hours of order confirmation" },
      "Processing timelines may be extended during:",
      { list: ["Public holidays", "Festivals", "Sale events", "Inventory audits", "Force majeure situations", "Operational disruptions beyond our reasonable control"] },
      "Order processing time is separate from shipping and delivery timelines.",
      "AutoCareX reserves the right to refuse, delay, or cancel any order at its sole discretion.",
    ]},
    { id: "shipping-policy", title: "3. Shipping Policy", blocks: [
      { sub: "Domestic Shipping (India)" },
      "Orders delivered within India are generally expected to arrive within:",
      { strong: "3-7 business days" },
      "from the date of dispatch.",
      "Delivery timelines are estimates only and are not guaranteed.",
      { sub: "International Shipping" },
      "AutoCareX ships to eligible international destinations worldwide.",
      "International delivery timelines vary depending on:",
      { list: ["Destination country", "Customs clearance procedures", "Local courier operations", "Regulatory requirements", "International logistics conditions"] },
      "AutoCareX is not responsible for delays caused by customs authorities, border inspections, local postal services, or international courier networks.",
    ]},
    { id: "shipping-charges", title: "4. Shipping Charges", blocks: [
      { sub: "Free Shipping" },
      "Free shipping is available on orders with a total cart value of:",
      { strong: "\u20B9499 or above" },
      { sub: "Orders Below \u20B9499" },
      "Orders below \u20B9499 are subject to:",
      { strong: "\u20B9100 shipping charges" },
      "Shipping fees are displayed during checkout and may be modified by AutoCareX without prior notice.",
    ]},
    { id: "customs-duties", title: "5. Customs Duties, Import Taxes & International Fees", blocks: [
      "For international shipments, customers shall be solely responsible for:",
      { list: ["Customs duties", "Import taxes", "VAT/GST", "Local governmental levies", "Customs clearance charges", "Brokerage fees", "Regulatory charges"] },
      "These charges are imposed by destination-country authorities and are outside AutoCareX's control.",
      "If a customer refuses delivery due to customs charges or import duties, AutoCareX shall not be responsible for any resulting losses, delays, penalties, or return shipping costs.",
    ]},
    { id: "shipping-partners", title: "6. Shipping Partners", blocks: [
      "Orders may be shipped using one or more of the following logistics providers:",
      { list: ["Delhivery", "Blue Dart"] },
      "AutoCareX reserves the right to engage alternative shipping partners whenever required.",
    ]},
    { id: "order-tracking", title: "7. Order Tracking", blocks: [
      "Once an order is dispatched, customers will receive tracking information through available communication channels including:",
      { list: ["Email", "SMS", "WhatsApp", "Customer account dashboard"] },
      "Customers are responsible for monitoring shipment progress using the tracking information provided.",
    ]},
    { id: "delivery-attempts", title: "8. Delivery Attempts", blocks: [
      "Courier partners generally make up to:",
      { strong: "Two (2) delivery attempts" },
      "at the shipping address provided by the customer.",
      "If delivery cannot be completed because of:",
      { list: ["Customer unavailability", "Incorrect address", "Refusal to accept delivery", "Failure to respond to courier communications"] },
      "the shipment may be marked as Return to Origin (RTO).",
      "Any additional shipment requests after RTO may require payment of applicable re-shipping charges.",
    ]},
    { id: "customer-responsibility-upon-delivery", title: "9. Customer Responsibility Upon Delivery", blocks: [
      "Customers must inspect their package immediately upon receipt.",
      "Any issue involving:",
      { list: ["Damaged products", "Tampered packaging", "Missing items", "Incorrect products", "Leakage or breakage"] },
      "must be reported to AutoCareX within:",
      { strong: "48 hours of delivery" },
      "Claims reported after this period may be rejected.",
      "Customers are strongly encouraged to retain all original packaging materials until inspection is complete.",
    ]},
    { id: "order-cancellation-policy", title: "10. Order Cancellation Policy", blocks: [
      "Customers may request order cancellation by contacting:",
      "Email: help@autocarex.in",
      "Cancellation requests can only be accommodated if the order has not yet been dispatched.",
      "Once an order has been shipped, cancellation cannot be guaranteed.",
      "AutoCareX reserves the right to cancel any order due to:",
      { list: ["Stock unavailability", "Pricing errors", "Fraud detection", "Regulatory restrictions", "Payment verification concerns", "Shipping limitations", "Operational issues"] },
      "Where payment has already been received, eligible refunds shall be processed in accordance with this Policy.",
    ]},
    { id: "return-eligibility", title: "11. Return Eligibility", blocks: [
      "Customers may request a return within:",
      { strong: "7 calendar days from the date of delivery" },
      "subject to compliance with all conditions outlined in this Policy.",
      "Return requests are accepted only for:",
      { list: ["Wrong product delivered", "Product received in damaged condition", "Manufacturing defect", "Missing items in shipment"] },
      "All return requests are subject to verification and approval by AutoCareX.",
    ]},
    { id: "change-of-mind", title: "12. Change of Mind Returns Not Accepted", blocks: [
      "AutoCareX does not accept returns, replacements, refunds, or store credit requests for:",
      { list: ["Change of mind", "Product no longer required", "Accidental purchases", "Customer preference", "Incorrect product selection by customer", "Failure to read product specifications", "Failure to verify product compatibility before purchase"] },
      "Customers are responsible for reviewing product descriptions and suitability before placing an order.",
    ]},
    { id: "mandatory-evidence-requirements", title: "13. Mandatory Evidence Requirements", blocks: [
      "To submit a return request, customers must provide:",
      { sub: "Mandatory Unboxing Video" },
      "A complete and uninterrupted unboxing video is mandatory for all claims involving:",
      { list: ["Damaged products", "Missing items", "Wrong products", "Manufacturing defects"] },
      "The video must clearly capture:",
      { list: ["Sealed package before opening", "Shipping label", "Package condition", "Entire unboxing process", "Product condition immediately upon opening"] },
      { sub: "Additional Supporting Evidence" },
      "Customers may also be required to provide:",
      { list: ["Product photographs", "Packaging photographs", "Proof of damage", "Order details", "Additional information requested by AutoCareX"] },
      "Claims submitted without sufficient supporting evidence may be rejected.",
    ]},
    { id: "conditions-for-approved-returns", title: "14. Conditions for Approved Returns", blocks: [
      "To qualify for return approval, products must:",
      { list: ["Be unused", "Be in original condition", "Include original packaging", "Include all accessories", "Include all labels and inserts", "Be free from customer-caused damage"] },
      "AutoCareX reserves the right to reject any return that fails to satisfy these conditions.",
    ]},
    { id: "non-returnable-products", title: "15. Non-Returnable Products", blocks: [
      "The following products are not eligible for return, replacement, refund, or store credit:",
      { list: ["Opened products", "Used products", "Consumable products", "Products damaged due to misuse", "Products damaged due to improper application", "Products altered or tampered with", "Products with missing packaging", "Products missing accessories or labels", "Clearance or promotional items", "Products damaged after delivery", "Products returned without mandatory unboxing video", "Products returned without supporting evidence", "Products subject to fraudulent or misleading claims"] },
      "AutoCareX retains sole discretion regarding return eligibility.",
    ]},
    { id: "return-shipping-costs", title: "16. Return Shipping Costs", blocks: [
      "Where a return request is approved, AutoCareX shall arrange or bear reasonable return shipping costs.",
      "Customers must not independently return products unless specifically instructed by AutoCareX.",
      "Unauthorized return shipments may be refused.",
    ]},
    { id: "inspection-of-returned-products", title: "17. Inspection of Returned Products", blocks: [
      "All returned products are subject to quality inspection upon receipt.",
      "Approval of a return request does not automatically guarantee a refund, replacement, or store credit.",
      "If inspection reveals:",
      { list: ["Product use", "Product tampering", "Customer-caused damage", "Missing contents", "False claims", "Misrepresentation"] },
      "AutoCareX reserves the right to deny the claim.",
    ]},
    { id: "refunds-replacements-store-credit", title: "18. Refunds, Replacements & Store Credit", blocks: [
      "Upon approval of a valid claim, AutoCareX may provide one of the following remedies:",
      { list: ["Refund", "Replacement", "Store Credit"] },
      "The remedy offered shall be determined at AutoCareX's sole discretion based upon:",
      { list: ["Nature of the claim", "Product condition", "Inspection findings", "Stock availability", "Operational considerations"] },
    ]},
    { id: "refund-processing", title: "19. Refund Processing", blocks: [
      "Approved refunds are generally processed within:",
      { strong: "7-14 business days" },
      "after final approval.",
      "Refund timelines may vary depending upon:",
      { list: ["Banks", "Payment gateways", "Financial institutions", "International payment providers"] },
      "AutoCareX shall not be responsible for delays caused by third-party financial service providers.",
    ]},
    { id: "cod-refunds", title: "20. COD Refunds", blocks: [
      "For Cash on Delivery (COD) orders, approved refunds will generally be processed through:",
      { strong: "UPI transfer" },
      "Customers may be required to provide valid UPI details for refund processing.",
    ]},
    { id: "fraud-prevention-chargeback", title: "21. Fraud Prevention & Chargeback Policy", blocks: [
      "AutoCareX actively investigates suspicious claims and reserves the right to reject requests involving:",
      { list: ["Fraudulent activity", "Product switching", "Tampered evidence", "False damage claims", "Return abuse", "Misrepresentation of facts"] },
      "Customers agree to first contact AutoCareX and follow the official resolution process before initiating any payment dispute, chargeback, reversal, or claim through a bank, card issuer, payment gateway, or financial institution.",
      "Submission of a chargeback or payment dispute without first utilizing AutoCareX's official resolution process may result in:",
      { list: ["Account suspension", "Restriction of future purchases", "Permanent account termination", "Submission of supporting evidence to financial institutions and payment processors"] },
    ]},
    { id: "limitation-of-liability", title: "22. Limitation of Liability", blocks: [
      "To the fullest extent permitted by applicable law, AutoCareX shall not be liable for:",
      { list: ["Delivery delays", "Courier disruptions", "Customs delays", "Lost profits", "Business interruption", "Indirect damages", "Consequential damages", "Product misuse", "Failure to follow product instructions"] },
      "In all circumstances, AutoCareX's total liability shall not exceed the purchase price paid for the specific product giving rise to the claim.",
    ]},
    { id: "force-majeure", title: "23. Force Majeure", blocks: [
      "AutoCareX shall not be liable for delays or failures caused by events beyond reasonable control, including:",
      { list: ["Natural disasters", "Floods", "Fires", "Earthquakes", "Strikes", "Lockouts", "Government actions", "Transportation failures", "Customs delays", "Internet outages", "Cybersecurity incidents", "Pandemic-related restrictions"] },
    ]},
    { id: "policy-changes", title: "24. Policy Changes", blocks: [
      "AutoCareX reserves the right to amend, modify, or replace this Policy at any time without prior notice.",
      "Updated versions shall become effective immediately upon publication on the Website.",
      "Continued use of the Website constitutes acceptance of the revised Policy.",
    ]},
    { id: "contact-information", title: "25. Contact Information", blocks: [
      "For delivery, shipping, return, replacement, or refund-related inquiries:",
      { contact: true },
      "By placing an order on AutoCareX, you acknowledge that you have read, understood, and agreed to this Delivery, Shipping, Return & Refund Policy.",
    ]},
  ],
};

export const COMPANY_INFO = COMPANY;
export const LEGAL = { privacy, cookie, terms, delivery };
export default LEGAL;
